import { useEffect } from "react"
import { Target, Trophy } from "lucide-react"
import { useStrategyStore } from "@/stores/strategy-store"
import type { GngDecision } from "@/stores/strategy-store"
import { OverviewStatTile } from "@/components/overview/OverviewStatTile"
import { strategyTabPath } from "@/lib/strategy-tabs"
import { RECOMMENDATION_LABEL } from "@/lib/gng-score"

interface OverviewStrategySummaryProps {
  bidId: string
}

const decisionConfig: Record<GngDecision, { label: string; color: string }> = {
  go: { label: RECOMMENDATION_LABEL.go, color: 'text-[hsl(var(--status-success))]' },
  conditional_go: { label: RECOMMENDATION_LABEL.conditional_go, color: 'text-[hsl(var(--status-warning))]' },
  no_go: { label: RECOMMENDATION_LABEL.no_go, color: 'text-[hsl(var(--destructive))]' },
}

/**
 * Strategy stats for the bid overview — Go/No-Go decision + Win Themes — as
 * compact tiles linking through to the Strategy tab's Go/No-Go and Win Themes views. Renders
 * nothing until strategy data exists. Emits 0–2 tiles (grid items) via a fragment.
 */
export function OverviewStrategySummary({ bidId }: OverviewStrategySummaryProps) {
  const { assessment, themes, coverageMatrix, currentBidId, loadAssessment, loadThemes, loadCoverageMatrix } = useStrategyStore()

  // The store is shared across bids, so "already has data" is only a reason to
  // skip loading when that data belongs to THIS bid. Keyed on emptiness alone,
  // a store populated from a previously-viewed bid made this condition false
  // forever and pinned that bid's Go/No-Go verdict onto every later bid's
  // overview — one of the two cases that never self-corrected.
  //
  // A null `currentBidId` is not stale: the store simply has not been scoped to
  // a bid yet (nothing here loads an assessment), and treating that as stale
  // would blank the tiles for data that is perfectly valid.
  const isStale = currentBidId !== null && currentBidId !== bidId

  useEffect(() => {
    if (isStale || (!assessment && !themes.length)) {
      void loadAssessment(bidId)
      void loadThemes(bidId)
      void loadCoverageMatrix(bidId)
    }
  }, [bidId, isStale, assessment, themes.length, loadAssessment, loadThemes, loadCoverageMatrix])

  const hasDecision = assessment?.decision != null
  const hasThemes = themes.length > 0

  // Render nothing while the store still holds another bid's strategy data —
  // showing it under this bid's URL is the bug, and it links through to this
  // bid's tabs, which makes it read as this bid's verdict.
  if (isStale) return null
  if (!hasDecision && !hasThemes) return null

  return (
    <>
      {hasDecision && assessment?.decision && (
        <OverviewStatTile
          icon={Target}
          label="Go / No-Go"
          value={
            <span className={decisionConfig[assessment.decision].color}>
              {decisionConfig[assessment.decision].label}
            </span>
          }
          sub={`Score ${assessment.overallScore ?? 0} / 100`}
          to={strategyTabPath(bidId, 'go-no-go')}
        />
      )}
      {hasThemes && (
        <OverviewStatTile
          icon={Trophy}
          label="Win themes"
          value={themes.length}
          sub={coverageMatrix && coverageMatrix.totalCriteria > 0 ? `${coverageMatrix.coveragePercent}% coverage` : 'Defined'}
          to={strategyTabPath(bidId, 'win-themes')}
        />
      )}
    </>
  )
}
